import React from 'react';
import { motion, AnimatePresence, Variants, HTMLMotionProps, Transition } from 'framer-motion';
import { tv, type VariantProps } from 'tailwind-variants';

// Define animation variants
const animationVariants: Record<string, Variants> = {
  fade: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  },
  fadeUp: {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
  },
  fadeDown: {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  },
  fadeLeft: {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  },
  fadeRight: {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
  },
  fadeIn: {
    hidden: { scale: 0.5, opacity: 0 },
    visible: { scale: 1, opacity: 1 },
    exit: { opacity: 0 },
  },

  scale: {
    hidden: { scale: 0, opacity: 0 },
    visible: { scale: 1, opacity: 1 },
    exit: { scale: 0, opacity: 0 },
  },
  scaleUp: {
    hidden: { scale: 0, opacity: 0, y: 20 },
    visible: { scale: 1, opacity: 1, y: 0 },
    exit: { scale: 0, opacity: 0, y: 20 },
  },
  rotate: {
    hidden: { rotate: -90, opacity: 0 },
    visible: { rotate: 0, opacity: 1 },
    exit: { rotate: 90, opacity: 0 },
  },
  bounce: {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 10,
      },
    },
    exit: { scale: 0, opacity: 0 },
  },
  slideUp: {
    hidden: { y: '100%', opacity: 0 },
    visible: { y: 0, opacity: 1 },
    exit: { y: '100%', opacity: 0 },
  },
  slideDown: {
    hidden: { y: '-100%', opacity: 0 },
    visible: { y: 0, opacity: 1 },
    exit: { y: '-100%', opacity: 0 },
  },
  slideLeft: {
    hidden: { x: '-100%', opacity: 0 },
    visible: { x: 0, opacity: 1 },
    exit: { x: '-100%', opacity: 0 },
  },
  slideRight: {
    hidden: { x: '100%', opacity: 0 },
    visible: { x: 0, opacity: 1 },
    exit: { x: '100%', opacity: 0 },
  },
  zoomLeft: {
    hidden: { scale: 0.5, opacity: 0, x: -100 },
    visible: { scale: 1, opacity: 1, x: 0 },
    exit: { scale: 0.5, opacity: 0, x: -100 },
  },
  zoomRight: {
    hidden: { scale: 0.5, opacity: 0, x: 100 },
    visible: { scale: 1, opacity: 1, x: 0 },
    exit: { scale: 0.5, opacity: 0, x: 100 },
  },
  zoomFadeLeft: {
    hidden: { scale: 0.8, opacity: 0, x: -50 },
    visible: { scale: 1, opacity: 1, x: 0 },
    exit: { scale: 0.8, opacity: 0, x: -50 },
  },
  zoomFadeRight: {
    hidden: { scale: 0.8, opacity: 0, x: 50 },
    visible: { scale: 1, opacity: 1, x: 0 },
    exit: { scale: 0.8, opacity: 0, x: 50 },
  },
  popLeft: {
    hidden: { scale: 0.2, opacity: 0, x: -80, rotate: -5 },
    visible: {
      scale: 1,
      opacity: 1,
      x: 0,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 15,
      },
    },
    exit: { scale: 0.2, opacity: 0, x: -80, rotate: -5 },
  },
  popRight: {
    hidden: { scale: 0.2, opacity: 0, x: 80, rotate: 5 },
    visible: {
      scale: 1,
      opacity: 1,
      x: 0,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 15,
      },
    },
    exit: { scale: 0.2, opacity: 0, x: 80, rotate: 5 },
  },
  pageTransition: {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 15 },
  },
};

// Define transition presets
const transitionPresets: Record<string, Transition> = {
  default: {
    duration: 0.3,
    ease: 'easeInOut',
  },
  fast: {
    duration: 0.15,
    ease: 'easeInOut',
  },
  medium: {
    duration: 0.4,
    ease: 'easeInOut',
  },
  slow: {
    duration: 0.6,
    ease: 'easeInOut',
  },
  spring: {
    type: 'spring',
    stiffness: 260,
    damping: 20,
  },
  bounce: {
    type: 'spring',
    stiffness: 300,
    damping: 10,
  },
  linear: {
    duration: 0.3,
    ease: 'linear',
  },
};

// Define tailwind classes for layout and styling
const animation = tv({
  base: 'relative',
  variants: {
    layout: {
      none: '',
      flex: 'flex',
      block: 'block',
      grid: 'grid',
      inline: 'inline',
      inlineFlex: 'inline-flex',
      inlineBlock: 'inline-block',
    },
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
      around: 'justify-around',
      evenly: 'justify-evenly',
    },
    gap: {
      none: 'gap-0',
      xs: 'gap-1',
      sm: 'gap-2',
      md: 'gap-4',
      lg: 'gap-6',
      xl: 'gap-8',
    },
    fullWidth: {
      true: 'w-full',
      false: '',
    },
    fullHeight: {
      true: 'h-full',
      false: '',
    },
  },
  defaultVariants: {
    layout: 'none',
    fullWidth: false,
    fullHeight: false,
  },
});

type AnimationCommonProps = VariantProps<typeof animation> & {
  children: React.ReactNode;
  effect?: keyof typeof animationVariants;
  transition?: keyof typeof transitionPresets;
  duration?: number;
  delay?: number;
  isVisible?: boolean;
  className?: string;
  onAnimationStart?: () => void;
  onAnimationComplete?: () => void;
  presenceMode?: 'sync' | 'wait';
  presenceKey?: string | number;
  disablePresence?: boolean;
} & Omit<
    HTMLMotionProps<'div'>,
    'initial' | 'animate' | 'exit' | 'variants' | 'transition' | 'onAnimationStart' | 'onAnimationComplete'
  >;

const AnimationCommon: React.FC<AnimationCommonProps> = ({
  children,
  layout,
  align,
  justify,
  gap,
  fullWidth,
  fullHeight,
  effect = 'fade',
  transition = 'default',
  duration,
  delay = 0,
  isVisible = true,
  className = '',
  onAnimationStart,
  onAnimationComplete,
  presenceMode = 'wait',
  presenceKey,
  disablePresence = false,
  ...rest
}) => {
  // Get the animation variant
  const selectedVariant = animationVariants[effect] || animationVariants.fade;

  // Get the transition preset
  let transitionConfig = { ...transitionPresets[transition] };

  // Override with specific duration and delay if provided
  if (duration !== undefined) {
    transitionConfig = { ...transitionConfig, duration };
  }

  if (delay !== undefined) {
    transitionConfig = { ...transitionConfig, delay };
  }

  const motionComponent = (
    <motion.div
      initial='hidden'
      animate='visible'
      exit='exit'
      variants={selectedVariant}
      transition={transitionConfig}
      className={animation({
        layout,
        align,
        justify,
        gap,
        fullWidth,
        fullHeight,
        className,
      })}
      onAnimationStart={onAnimationStart ? () => onAnimationStart() : undefined}
      onAnimationComplete={onAnimationComplete ? () => onAnimationComplete() : undefined}
      {...rest}
    >
      {children}
    </motion.div>
  );

  if (disablePresence) {
    return isVisible ? motionComponent : null;
  }

  return (
    <AnimatePresence mode={presenceMode} key={presenceKey}>
      {isVisible && motionComponent}
    </AnimatePresence>
  );
};

export default AnimationCommon;
